/* eslint-disable react-hooks/exhaustive-deps */
import { Check as CheckIcon, PriorityHigh as PriorityHighIcon } from '@mui/icons-material';
import {
    Grid,
    List,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { CollapsiblePaper, DataColumns } from 'cleveron-web-components';
import { toDurationStringFromSeconds } from 'cleveron-web-components/utils';
import React, { useEffect } from 'react';

import ChecklistComponent from './ChecklistComponent';
import TableComponent from './TableComponent';
import TextComponent from './TextComponent';

const useStyles = makeStyles(theme => ({
    back: {
        backgroundColor: theme.palette.background.paper,
    },
    data: {
        padding: theme.spacing(2),
        paddingLeft: 58,
    },
    dataTitle: {
        color: theme.palette.text.secondary,
    },
    item: {
        borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
        borderRadius: 0,
        boxShadow: 'none',
        margin: theme.spacing(0, 0, 1),
        paddingBottom: theme.spacing(1),
        paddingTop: 0,
    },
    itemComponent: {
        paddingBottom: `${theme.spacing(4)} !important`,
    },
    table: {
        '& td, th': {
            borderBottom: 'none',
            padding: theme.spacing(0.5, 1),
        },
        '& td:first-child, th': {
            color: theme.palette.text.secondary,
        },
        '& th': {
            textAlign: 'center',
        },
        '& tr:nth-of-type(even)': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    warningIcon: {
        color: theme.palette.warning.main,
        paddingBottom: 0,
    },
}));

function Item({
    disabled,
    id,
    item,
    issueId,
    completedItems,
    setCompletedItems,
    setHasProblemItems,
    hasProblemItems,
}) {
    const classes = useStyles();

    const info = [
        {
            key: 1,
            rows: [
                { key: 'eta', value: item.eta ? toDurationStringFromSeconds(item.eta) : '—' },
                item.period && { key: 'period', value: `${item.period} months` },
                item.cycles && { key: 'cycles', value: `${item.cycles} cycles` },
            ].filter(row => row),
        },
        {
            key: 2,
            rows: [{ key: 'description', value: item.description || '—' }],
        },
    ];

    const columnComponents = item.components.filter(
        component => component.componentType === 'COLUMN'
    );
    const rowComponents = item.components.filter(component => component.componentType === 'ROW');

    const isCompleted =
        (item.itemType === 'CHECKLIST' &&
            item.components.filter(component => component.completed === true).length ===
                item.components?.length) ||
        (item.itemType === 'TEXT_INPUT' &&
            item.components[0].value !== null &&
            item.components[0].value !== '') ||
        (item.itemType === 'TABLE' &&
            columnComponents.length * rowComponents.length === item.tableValues.length &&
            !item.tableValues.some(
                tableValue => tableValue.value === null || tableValue.value === ''
            ));

    const hasProblems =
        item.itemType === 'CHECKLIST' &&
        item.components.some(component => component.hasProblems === true);

    useEffect(() => {
        if (isCompleted && !completedItems?.includes(item.id)) {
            setCompletedItems([...completedItems, item.id]);
        } else if (!isCompleted && completedItems?.includes(item.id)) {
            setCompletedItems(completedItems.filter(completed => completed !== item.id));
        }
        if (hasProblems && !hasProblemItems?.includes(item.id)) {
            setHasProblemItems([...hasProblemItems, item.id]);
        } else if (!hasProblems && hasProblemItems?.includes(item.id)) {
            setHasProblemItems(hasProblemItems.filter(completed => completed !== item.id));
        }
    }, [isCompleted, hasProblems]);

    return (
        <CollapsiblePaper
            className={classes.item}
            hasMoveDeleteButtons={false}
            headerComponent={
                <Grid alignItems="center" container item spacing={2} xs={12}>
                    <Grid item xs="auto">
                        <Typography variant="h6">{item.name}</Typography>
                    </Grid>
                    {hasProblems && (
                        <Grid item xs="auto">
                            <PriorityHighIcon className={classes.warningIcon} />
                        </Grid>
                    )}
                    {!hasProblems && isCompleted && (
                        <Grid item xs="auto">
                            <CheckIcon color="secondary" sx={{ mb: '-6px' }} />
                        </Grid>
                    )}
                </Grid>
            }
        >
            <Grid container spacing={2}>
                <Grid item md xs={12}>
                    <DataColumns
                        alignItems="flex-start"
                        columns={info}
                        id={`${id}Info`}
                        titleClassName={classes.dataTitle}
                        translationKey="task.maintenance"
                    />
                </Grid>
                <Grid className={classes.itemComponent} item md={12} xs={12}>
                    {item.itemType === 'CHECKLIST' && (
                        <List>
                            {item.components.map(component => (
                                <ChecklistComponent
                                    data={component}
                                    disabled={disabled}
                                    id={`${id}${component.id}Component`}
                                    issueId={issueId}
                                    itemId={item.id}
                                    key={`${item}-${component}`}
                                />
                            ))}
                        </List>
                    )}
                    {item.itemType === 'TABLE' && (
                        <Table className={classes.table} id={`${id}Table`}>
                            <TableHead id={`${id}Head`}>
                                <TableRow id={`${id}Row`}>
                                    <TableCell id={`${id}Cell`} />
                                    {columnComponents.map(column => (
                                        <TableCell id={`${id}${column.id}Cell`} key={column.id}>
                                            {column.title}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody id={`${id}Body`}>
                                {rowComponents.map(row => (
                                    <TableRow key={row.id}>
                                        <TableCell id={`${id}${row.id}Cell`}>{row.title}</TableCell>
                                        {columnComponents.map(column => (
                                            <TableCell
                                                id={`${id}${row.id}${column.id}Cell`}
                                                key={column.id}
                                            >
                                                <TableComponent
                                                    columnId={column.id}
                                                    disabled={disabled}
                                                    id={`${id}${row.id}${column.id}TableComponent`}
                                                    issueId={issueId}
                                                    itemId={item.id}
                                                    key={`${row.id}${column.id}`}
                                                    rowId={row.id}
                                                    value={item.tableValues
                                                        ?.filter(
                                                            value =>
                                                                value.rowId === row.id &&
                                                                value.columnId === column.id
                                                        )
                                                        .map(value => value.value)}
                                                />
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                    {item.itemType === 'TEXT_INPUT' && (
                        <TextComponent
                            component={item.components[0]}
                            disabled={disabled}
                            id={`${id}TextComponent`}
                            issueId={issueId}
                            itemId={item.id}
                        />
                    )}
                </Grid>
            </Grid>
        </CollapsiblePaper>
    );
}

export default Item;
